<template>
    <div>
        <div class="w-full overflow-x-auto flex justify-center">
            <div
                ref="labelTemplate"
                class="w-half-letter min-h-half-letter flex-shrink-0 flex flex-col bg-white text-black p-8"
            >
                <div
                    class="flex items-end justify-between border-b-4 border-black pb-3"
                >
                    <span class="text-xl font-bold uppercase">
                        Hoja de envío
                    </span>
                    <span class="text-3xl font-bold">
                        Orden #{{ order.id }}
                    </span>
                </div>

                <div class="mt-6 flex flex-col gap-2">
                    <span class="text-lg font-bold uppercase tracking-wide">
                        Destinatario
                    </span>
                    <span class="text-4xl font-bold leading-tight break-words">
                        {{ recipient.name }}
                    </span>
                    <span v-if="recipient.phone" class="text-3xl">
                        Tel. {{ recipient.phone }}
                    </span>
                    <span class="mt-2 text-3xl leading-snug break-words">
                        {{ recipient.address }}
                    </span>
                    <span
                        v-if="recipient.addressComplement"
                        class="text-2xl leading-snug break-words"
                    >
                        {{ recipient.addressComplement }}
                    </span>
                    <span
                        v-if="recipient.neighborhood"
                        class="text-2xl break-words"
                    >
                        Barrio {{ recipient.neighborhood }}
                    </span>
                    <span
                        v-if="recipient.city"
                        class="mt-2 text-3xl font-bold uppercase break-words"
                    >
                        {{ recipient.city }}
                    </span>
                </div>

                <div
                    class="mt-auto pt-6 border-t-2 border-dashed border-black flex flex-col gap-1"
                >
                    <span class="text-base font-bold uppercase tracking-wide">
                        Remitente
                    </span>
                    <span
                        v-if="sender.name"
                        class="text-2xl font-bold break-words"
                    >
                        {{ sender.name }}
                    </span>
                    <span v-if="sender.phone" class="text-xl">
                        Tel. {{ sender.phone }}
                    </span>
                    <span v-if="sender.address" class="text-xl break-words">
                        {{ sender.address }}
                    </span>
                    <span v-if="sender.city" class="text-xl break-words">
                        {{ sender.city }}
                    </span>
                </div>
            </div>
        </div>

        <div class="mt-4 w-full flex justify-center">
            <pdf-download-button @click="generatePdf" />
        </div>
    </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";

import { resolveOrderCustomer } from "@/common/lib/order-customer";
import {
    HALF_LETTER_FORMAT_MM,
    downLoadPDF,
    elementToPDF,
    openPDF
} from "@/common/lib/pdf";
import { getWhatsappCountry, splitWhatsappNumber } from "@/common/lib/whatsapp";

import PdfDownloadButton from "@/components/utils/PdfDownloadButton.vue";

const joinParts = (parts, separator = " ") =>
    parts
        .map((part) => (part || "").toString().trim())
        .filter(Boolean)
        .join(separator);

export default {
    name: "ShippingLabelPrinter",
    components: { PdfDownloadButton },
    props: {
        order: {
            type: Object,
            required: true
        }
    },
    data: () => ({
        tenantName: "",
        tenantPhone: ""
    }),
    computed: {
        ...mapGetters("control", ["backgroundColor"]),
        recipient() {
            const location = this.order.location || {};
            const customer = resolveOrderCustomer(this.order) || {};
            return {
                name:
                    location.receiverName ||
                    joinParts([customer.firstName, customer.lastName]),
                phone: location.receiverPhone || customer.phone || "",
                address: location.address || "",
                addressComplement: location.addressComplement || "",
                neighborhood: location.neighborhood || "",
                city: location.city || ""
            };
        },
        sender() {
            const merchant = this.order.supplyMerchant || {};
            return {
                name: this.tenantName || merchant.name || "",
                phone: merchant.phone || this.tenantPhone,
                address: joinParts(
                    [merchant.address, merchant.addressComplement],
                    " - "
                ),
                city: merchant.address ? merchant.city || "" : ""
            };
        },
        filename() {
            return `hoja_envio_${this.order.id}`;
        }
    },
    async mounted() {
        await this.loadTenantInfo();
    },
    methods: {
        ...mapActions("tenants", ["getCachedTenantBrandingSettings"]),
        async loadTenantInfo() {
            try {
                const branding = await this.getCachedTenantBrandingSettings();
                this.tenantName = branding?.name || "";
                this.tenantPhone = this.formatPhone(
                    branding?.whatsappNumber,
                    branding?.defaultCountry
                );
            } catch {
                this.tenantName = "";
                this.tenantPhone = "";
            }
        },
        formatPhone(fullNumber, defaultCountry) {
            if (!fullNumber) return "";
            const { country, localNumber } = splitWhatsappNumber(
                fullNumber,
                defaultCountry
            );
            if (!localNumber) return "";
            const dialCode = getWhatsappCountry(country)?.dialCode;
            return country === "CO" || !dialCode
                ? localNumber
                : `+${dialCode} ${localNumber}`;
        },
        async generatePdf() {
            try {
                const pdf = await this.$loading.wrap(() =>
                    elementToPDF(this.$refs.labelTemplate, {
                        format: HALF_LETTER_FORMAT_MM
                    })
                );
                if (this.$isPhone) {
                    downLoadPDF(pdf, this.filename);
                } else {
                    openPDF(pdf, this.filename);
                }
            } catch (error) {
                this.showGenerationError(error);
            }
        },
        showGenerationError(error) {
            console.error("Error generando el PDF", error);
            this.$swal.fire({
                icon: "error",
                title: "No pudimos generar el PDF",
                text:
                    error?.message ||
                    (typeof error === "string" && error) ||
                    "Intenta de nuevo en unos segundos.",
                background: this.backgroundColor
            });
        }
    }
};
</script>
